/**
 * Shared auth state cleanup used by both `useInvalidGrantHandler` (runtime
 * recovery) and `useSignInOut.executeSignOut` (manual sign-out).
 *
 * Centralizing the cleanup ensures the critical ordering is applied consistently:
 *
 *   1. Flip local auth state and clear the app user cache first, so any query
 *      that re-reads context sees "not authenticated" immediately.
 *   2. `await queryClient.cancelQueries()` BEFORE `queryClient.clear()`.
 *      Without the cancel, in-flight requests resolve after the cache is wiped
 *      and write into observers that just got re-mounted, producing the
 *      stale-data cascade these recovery paths are designed to prevent.
 *      The cancel aborts observer state so a late response cannot re-hydrate
 *      a cleared query.
 *   3. Clear Logto tokens last. Wrapped in try/catch because a failure here
 *      shouldn't block the rest of the recovery flow.
 */

import type { QueryClient } from '@tanstack/react-query';
import { clearJustPosted } from '@lib/feed/justPosted';
import { logger } from '@utils/logger';

export interface ClearAuthStateOptions {
  queryClient: QueryClient;
  setAuthenticated: (v: boolean) => void;
  setCurrentUser: (u: null) => void;
  /** Removes the cached current-user record from the React Query cache. */
  clearUser: () => void;
  /** Logto SDK `clearAllTokens`. Errors are swallowed + logged. */
  clearAllTokens: () => Promise<void>;
}

/**
 * Run the shared auth cleanup sequence. Always awaits the query cancel so
 * callers can rely on "no stale writes after this resolves".
 */
export async function clearAuthState(options: ClearAuthStateOptions): Promise<void> {
  const { queryClient, setAuthenticated, setCurrentUser, clearUser, clearAllTokens } = options;

  // Step 1: Flip local auth state synchronously so downstream consumers see it.
  setAuthenticated(false);
  setCurrentUser(null);
  clearUser();

  // Step 2: Cancel before clear. clear() alone lets in-flight requests
  // re-hydrate cleared observers, triggering the cascade described in
  // the module docstring above.
  try {
    await queryClient.cancelQueries();
  } catch (err) {
    // cancelQueries shouldn't reject in practice, but don't block recovery if it does.
    logger.warn('[clearAuthState] cancelQueries rejected unexpectedly:', err);
  }
  queryClient.clear();
  // The just-posted registry is auth-scoped MODULE state (its stored posts carry
  // is_my_question: true), so queryClient.clear() cannot reach it. Without this,
  // the previous user's post gets spliced into the next session's meetverse feed
  // with owner-only affordances.
  clearJustPosted();

  // Step 3: Clear Logto tokens. Failures are logged but not rethrown so the
  // recovery flow can still transition into terminal-state handling.
  try {
    await clearAllTokens();
  } catch (err) {
    logger.error('[clearAuthState] clearAllTokens failed:', err);
  }
}
